/**
 * Client-side shapes for questions the agent asks with the AskUser tool. The questions are carried on
 * the assistant message as `askUserRequest`, and the user's response on their next message as
 * `askUserResponse`; see benchling/llm/tools/ask_user_tool.py.
 */
import {AskUserResponseStatus} from 'graphql-client/__generated/graphqlGlobalTypes';

export enum AskUserQuestionType {
  SINGLE_SELECT = 'single_select',
  MULTI_SELECT = 'multi_select',
  FREE_TEXT = 'free_text',
}

export interface AskUserSelectOption {
  label: string;
  description?: string;
}

interface AskUserQuestionBase {
  /** Stable key that answers are returned under. */
  id: string;
  header: string;
  question: string;
}

export interface AskUserSelectQuestion extends AskUserQuestionBase {
  type: AskUserQuestionType.SINGLE_SELECT | AskUserQuestionType.MULTI_SELECT;
  options: Array<AskUserSelectOption>;
}

export interface AskUserFreeTextQuestion extends AskUserQuestionBase {
  type: AskUserQuestionType.FREE_TEXT;
  placeholder?: string;
}

export type AskUserQuestion = AskUserSelectQuestion | AskUserFreeTextQuestion;

export type AskUserAnswer =
  | {type: AskUserQuestionType.SINGLE_SELECT; selected: string}
  | {type: AskUserQuestionType.MULTI_SELECT; selected: Array<string>}
  | {type: AskUserQuestionType.FREE_TEXT; text: string};

export interface AskUserResponse {
  status: AskUserResponseStatus;
  /** Answers keyed by question id; empty unless answered. */
  answers: Record<string, AskUserAnswer>;
}

/**
 * The user's next message after the questions, or null while they are still open. `response` is
 * null when that message did not come from the questions card.
 */
export type AskUserReply = {response: AskUserResponse | null} | null;

/** The user's in-progress answer to one question. */
export interface AskUserDraft {
  selectedLabels: Array<string>;
  isOtherSelected: boolean;
  otherText: string;
  text: string;
}
